import { createSelector } from 'reselect';

import {
  CollectionRestaurantDetail,
  CollectionRestaurantSummary,
  DeliveryRestaurantDetail,
  DeliveryRestaurantSummary,
  MenuReposnse,
} from '../Api';
import { DeliveryKey } from '../Store';

export interface GetCollectionProps {
  vrId: number | string;
  prId: number | string;
}
export interface GetDeliveryProps {
  vrId: number | string;
  deliveryLocationId: number | string;
}
export type GetRestaurantProps =
  | {
      vrId: number | string;
      deliveryType: 'collection';
      prId: number | string;
      deliveryLocationId?: number | string;
    }
  | {
      vrId: number | string;
      deliveryType: 'delivery';
      deliveryLocationId: number | string;
      prId?: number | string;
    };

//#region MENU_BY_URL
export const getMenuByUrl = (
  state: WebOrder.State['flipdish']['MenuData'],
  props: { menuUrl: string },
) => state[props.menuUrl];
//#endregion

//#region RESTAURANT_DETAILS

export const getRestaurantDetails = (
  state: WebOrder.State,
  props: GetRestaurantProps,
) =>
  props.deliveryType === 'collection'
    ? getCollectionDetails(state.collectionRestaurants, props)
    : getDeliveryDetails(state.deliveryRestaurants, props);

export const getCollectionDetails = createSelector<
  WebOrder.State['collectionRestaurants'],
  GetCollectionProps,
  number,
  number,
  WebOrder.State['collectionRestaurants'],
  CollectionRestaurantDetail | undefined
>(
  [
    (state, props) => stringToNumber(props.vrId),
    (state, props) => stringToNumber(props.prId),
    (state, _props) => state,
  ],
  (vrId, prId, collectionDetails) => {
    if (!vrId || !prId) {
      return undefined;
    }

    const details = collectionDetails[prId];

    if (details && details.status === 'success') {
      return details.data;
    }

    return undefined;
  },
);

export const getDeliveryDetails = createSelector<
  WebOrder.State['deliveryRestaurants'],
  GetDeliveryProps,
  number,
  number,
  WebOrder.State['deliveryRestaurants'],
  DeliveryRestaurantDetail | undefined
>(
  [
    (state, props) => stringToNumber(props.vrId),
    (state, props) => stringToNumber(props.deliveryLocationId),
    (state, _props) => state,
  ],
  (vrId, deliveryLocationId, deliveryRestaurants) => {
    if (!vrId || !deliveryLocationId) {
      return undefined;
    }

    const key = DeliveryKey(vrId, deliveryLocationId);
    const details = deliveryRestaurants[key];
    if (details && details.status === 'success') {
      return details.data;
    }

    return undefined;
  },
);

//#endregion

//#region RESTAURANT_SUMMARY

export const getRestaurantSummary = (
  state: WebOrder.State,
  props: GetRestaurantProps,
) =>
  props.deliveryType === 'collection'
    ? getCollectionSummary(state.flipdish.CollectionSummaries, props)
    : getDeliverySummary(state.deliveryOptions, props);

export const getCollectionSummary = createSelector<
  WebOrder.State['flipdish']['CollectionSummaries'],
  GetCollectionProps,
  number,
  number,
  WebOrder.State['flipdish']['CollectionSummaries'],
  CollectionRestaurantSummary | null
>(
  [
    (state, props) => stringToNumber(props.vrId),
    (state, props) => stringToNumber(props.prId),
    (state, _props) => state,
  ],
  (vrId, prId, CollectionSummaries) => {
    if (!vrId || !prId) {
      return null;
    }

    if (CollectionSummaries && CollectionSummaries.status === 'success') {
      const config = CollectionSummaries.data.find(
        (s) =>
          s.VirtualRestaurantId === vrId && s.PhysicalRestaurantId === prId,
      );

      return config;
    }

    return null;
  },
);

export const getDeliverySummary = createSelector<
  WebOrder.State['deliveryOptions'],
  GetDeliveryProps,
  number,
  number,
  WebOrder.State['deliveryOptions'],
  DeliveryRestaurantSummary | null
>(
  [
    (state, props) => stringToNumber(props.vrId),
    (state, props) => stringToNumber(props.deliveryLocationId),
    (state, _props) => state,
  ],
  (vrId, deliveryLocationId, deliveryOptions) => {
    if (!vrId || !deliveryLocationId) {
      return null;
    }

    const deliveryOption = deliveryOptions[deliveryLocationId];
    if (deliveryOption && deliveryOption.status === 'success') {
      if (deliveryOption.data.type === 'skip') {
        return deliveryOption.data.to;
      } else {
        return deliveryOption.data.of.find(
          (o) => o.VirtualRestaurantId === vrId,
        );
      }
    }

    return null;
  },
);
//#endregion

type FindMenuProps = GetRestaurantProps;

export const findMenu = (state: WebOrder.State, props: FindMenuProps) =>
  props.deliveryType === 'collection'
    ? findCollectionMenu(
        {
          CollectionSummaries: state.flipdish.CollectionSummaries,
          MenuData: state.flipdish.MenuData,
        },
        props,
      )
    : findDeliveryMenu(
        {
          deliveryOptions: state.deliveryOptions,
          MenuData: state.flipdish.MenuData,
        },
        props,
      );

interface FindCollectionMenuProps {
  vrId: number | string;
  prId: number | string;
}
export const findCollectionMenu = createSelector<
  {
    CollectionSummaries: WebOrder.State['flipdish']['CollectionSummaries'];
    MenuData: WebOrder.State['flipdish']['MenuData'];
  },
  FindCollectionMenuProps,
  CollectionRestaurantSummary,
  WebOrder.State['flipdish']['MenuData'],
  MenuReposnse | null
>(
  [
    (state, props) => getCollectionSummary(state.CollectionSummaries, props),
    (state, _props) => state.MenuData,
  ],
  (CollectionSummary, MenuData) => {
    if (CollectionSummary) {
      const menuUrl = CollectionSummary.MenuUrl;

      if (!menuUrl) {
        return null;
      }

      const menu = getMenuByUrl(MenuData, { menuUrl });

      if (menu && menu.status === 'success') {
        return menu.data;
      }
    }

    return null;
  },
);

interface FindDeliveryMenuProps {
  vrId: number | string;
  deliveryLocationId: number | string;
}
export const findDeliveryMenu = createSelector<
  {
    deliveryOptions: WebOrder.State['deliveryOptions'];
    MenuData: WebOrder.State['flipdish']['MenuData'];
  },
  FindDeliveryMenuProps,
  DeliveryRestaurantSummary,
  WebOrder.State['flipdish']['MenuData'],
  MenuReposnse | null
>(
  [
    (state, props) => getDeliverySummary(state.deliveryOptions, props),
    (state, _props) => state.MenuData,
  ],
  (DeliveryConfig, MenuData) => {
    if (DeliveryConfig) {
      const menuUrl = DeliveryConfig.MenuUrl;

      if (!menuUrl) {
        return null;
      }

      const menu = getMenuByUrl(MenuData, { menuUrl });

      if (menu && menu.status === 'success') {
        return menu.data;
      }
    }

    return null;
  },
);

const menuDataSelector = (state) => state.MenuData;
interface FindMenuByUrlProps {
  menuUrl: string;
}
export const findMenuByUrl = createSelector<
  { MenuData: WebOrder.State['flipdish']['MenuData'] },
  FindMenuByUrlProps,
  WebOrder.State['flipdish']['MenuData'],
  string,
  MenuReposnse | null
>([menuDataSelector, (_state, props) => props.menuUrl], (MenuData, menuUrl) => {
  if (!menuUrl) {
    return null;
  }

  const menu = getMenuByUrl(MenuData, { menuUrl: menuUrl });
  if (menu && menu.status === 'success') {
    return menu.data;
  }

  return null;
});

function stringToNumber(val: string | number) {
  return typeof val === 'string' ? parseInt(val) : val;
}
